/**
 * Fixture fallback for the API modules that are specified but not built.
 *
 * `docs/API.md` runs to §17; `salescrmapi` implements §2 (auth) and §3 (users,
 * roles). Everything else answers `501 not_implemented` naming the section. The
 * screens for those sections still need to exist — they are the deliverable
 * being reviewed — so this catches that one error code and serves a fixture.
 *
 * Three properties make this safe rather than a lie the codebase tells itself:
 *
 *  1. **It only catches `not_implemented`.** A 403, a 500, a network failure or
 *     a validation error propagates untouched. A screen cannot accidentally look
 *     healthy while the API is broken.
 *  2. **It disappears on its own.** The moment `/v1/leads` returns real data the
 *     fixture stops being reached; nothing needs deleting for the switch to
 *     happen, and nothing silently shadows a working endpoint.
 *  3. **It is loud.** Every fallback logs once per endpoint and flags the result
 *     with `__mock: true`, which the UI renders as a visible badge. Nobody
 *     should be able to demo a fixture believing it is live data.
 *
 * Set `VITE_USE_MOCKS=false` to turn it off and see the raw 501s.
 */
import { ApiError } from './axiosClient.js';

const MOCKS_ENABLED = import.meta.env.VITE_USE_MOCKS !== 'false';

/** Endpoints already warned about, so the console shows one line each, not one per keystroke. */
const warned = new Set();

/**
 * @template T
 * @param {string} label endpoint name, for the warning and the badge
 * @param {() => Promise<T>} live the real call
 * @param {() => T} fixture what to serve while the endpoint is a 501
 * @returns {Promise<T>}
 */
export async function withMockFallback(label, live, fixture) {
  try {
    return await live();
  } catch (error) {
    const isNotBuilt = error instanceof ApiError && error.code === 'not_implemented';
    if (!isNotBuilt || !MOCKS_ENABLED) throw error;

    if (!warned.has(label)) {
      warned.add(label);
      // eslint-disable-next-line no-console
      console.warn(
        `[mock] ${label} — the API answered 501, serving a fixture. ` +
          `This screen is not showing live data.`,
      );
    }

    const data = fixture();
    return tag(data);
  }
}

/** Mark a payload as fixture-derived so the UI can badge it. */
function tag(data) {
  if (Array.isArray(data)) {
    Object.defineProperty(data, '__mock', { value: true, enumerable: false });
    return data;
  }
  if (data && typeof data === 'object') return { ...data, __mock: true };
  return data;
}

/** True when a query result came from a fixture rather than the API. */
export const isMock = (result) => Boolean(result?.__mock);

/**
 * Paginate a fixture array the way `docs/API.md` §1.4 paginates a real list, so
 * screens exercise cursor pagination against fixtures too and do not need
 * rewriting when the endpoint arrives.
 *
 * @param {object[]} rows
 * @param {{limit?:number, cursor?:string}} params
 * @param {(row:object)=>string} idOf
 */
export function paginate(rows, { limit = 50, cursor } = {}, idOf = (r) => r.id) {
  const start = cursor ? rows.findIndex((r) => idOf(r) === decodeCursor(cursor)) + 1 : 0;
  const slice = rows.slice(start, start + limit);
  const hasMore = start + limit < rows.length;
  return {
    data: slice,
    page: {
      limit,
      next_cursor: hasMore && slice.length ? encodeCursor(idOf(slice[slice.length - 1])) : null,
      prev_cursor: null,
      has_more: hasMore,
      total: rows.length,
    },
  };
}

const encodeCursor = (id) => btoa(JSON.stringify({ id }));
const decodeCursor = (cursor) => {
  try {
    return JSON.parse(atob(cursor)).id;
  } catch {
    return null;
  }
};

/**
 * Apply the simple query filters the list screens use, against a fixture array.
 * Mirrors the equality-and-search filtering `docs/API.md` §1.5 specifies for
 * query parameters — not the filter-tree grammar, which belongs to `POST /query`.
 *
 * @param {object[]} rows
 * @param {object} params raw query params from the screen
 * @param {{search?:string[], equals?:string[]}} config which fields each param touches
 */
export function applyFilters(rows, params = {}, config = {}) {
  let out = rows;

  if (params.q && config.search?.length) {
    const needle = String(params.q).toLowerCase();
    out = out.filter((row) =>
      config.search.some((field) => String(row[field] ?? '').toLowerCase().includes(needle)),
    );
  }

  for (const field of config.equals ?? []) {
    const value = params[field];
    if (value === undefined || value === '' || value === 'all') continue;
    const wanted = String(value).split(',').map((v) => v.trim().toLowerCase());
    out = out.filter((row) => wanted.includes(String(row[field] ?? '').toLowerCase()));
  }

  return out;
}
